<template>
  <div>
    <v-container class="pt-3 pb-6 px-6">

      <!-- Floating Button -->
      <floating-button @click="contractCreate()" v-if="authUser"/>

      <!-- Confirmation -->
      <confirmation
        v-if="authUser"
        v-model="dialog"
        title="Eliminar Adjudicación"
        text="Desea eliminar la adjudicación"
        :item="contractToDelete.client_code"
        @close="dialog = false"
        @confirm="deleteContract()"
      />

      <!-- Page Header -->
      <page-header
        title="Adjudicaciones"
        icon="mdi-format-list-bulleted"
        divider="-"
        :breadcrumbs="breadcrumbs.items"
        refresh
        @refresh="loadContracts()"

      ></page-header>
      
      <!-- Content -->
      <v-row align="center" justify="center">
        <!-- Table -->
        <v-col cols="12">
          <!-- Loading Bar -->
          <v-progress-linear
            indeterminate
            v-if="isLoadingData"
          ></v-progress-linear>
          <!-- Table -->
          <v-data-table
            v-if="authUser"
            :headers="headers"
            :items="filteredContractsByState"
            :items-per-page="5"
            class="elevation-1"
            :search="filterInput"
            :expanded.sync="expanded"
            single-expand
            item-key="id"
            show-expand
            :footer-props="{
              'items-per-page-options': [5, 15, 30, 50]
            }"
          >
            <!-- Top -->
            <template v-slot:top>
              <v-toolbar flat color="white">
                <!-- Date Filter -->
                <v-menu
                  v-model="menu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      label="Fecha"
                      :value="formatDate(dateSelected)"
                      class="shrink mr-5"
                      readonly
                      clearable
                      hide-details
                      v-on="on"
                      @click:clear="dateSelected = ''"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="dateSelected" @change="menu = false"></v-date-picker>
                </v-menu>
                <!-- State Filter -->
                <v-select
                  v-model="filterState"
                  :items="stateItems"
                  class="shrink mr-5"
                  label="Estado"
                  hide-details
                  clearable
                  single-line
                ></v-select>
                <!-- Input Filter -->
                <v-text-field
                  v-model="filterInput"
                  class="grow"
                  label="Buscar..."
                  prepend-icon="mdi-magnify"
                  clearable
                  single-line
                  hide-details 
                ></v-text-field>
              </v-toolbar>
            </template>
            <!-- Summary -->
            <template v-slot:expanded-item="{ item, headers }">
              <td :colspan="headers.length">
                <span class="ma-3 font-weight-bold caption">Resumen</span> {{ item.summary }}
              </td>
            </template>
            <!-- Client ID -->
            <template v-slot:item.client_id="{ item }">
              {{ getClientName(item.client_id) }}
            </template>
            <!-- Contract Date -->
            <template v-slot:item.contract_date="{ item }">
              {{ formatDate(item.contract_date) }}
            </template>
            <!-- State -->
            <template v-slot:item.state="{ item }">
              <v-chip
                :color="item.state === 'PENDIENTE' ? '' : item.state === 'EN CURSO' ? 'primary' : 'deep-orange'"
                :class="item.state === 'PENDIENTE' ? '' : 'white--text'"
              >{{ item.state }}</v-chip>
            </template>
            <!-- Actions -->
            <template v-slot:item.action="{ item }">
              <v-icon class="mr-2" @click="contractEdit(item.id)">mdi-account-edit</v-icon>
              <v-icon @click="deleteContractDialog(item)">mdi-delete</v-icon>
            </template>
            <!-- No Data -->
            <template v-slot:no-data>
              No hay elementos
            </template>
            <!-- No Results -->
            <template v-slot:no-results>
              No hay elementos
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import Confirmation from "@/components/Confirmation";
import PageHeader from "@/components/PageHeader";
import Util from "@/util/index";

export default {
  components: {
    FloatingButton,
    Confirmation,
    PageHeader
  },
  data: () => ({
    expanded: [],
    authUser: false,
    isLoadingData: false,
    dialog: false,
    menu: false,
    dateSelected: '',
    contractToDelete: {},
    filterInput: '',
    filterState: '',
    stateItems: ['PENDIENTE', 'EN CURSO', 'FINALIZADA'],
    headers: [
      { text: 'Código de adjudicación', align: 'center', sortable: true, value: 'client_code' },
      { text: 'Nombre comercial', align: 'center', sortable: true, value: 'client_id' },
      { text: 'Fecha de adjudicación', align: 'center', sortable: true, value: 'contract_date' },
      { text: 'Estado', align: 'center', sortable: true, value: 'state' },
      { text: 'Acción', align: 'center', sortable: false, value: 'action' }
    ],
    breadcrumbs: {
      items: [
        { text: "Configuración", disabled: true },
        { text: "Adjudicaciones", disabled: true }
      ]
    }
  }),
  computed: {
    clients () {
      return this.$store.state.clients.clients
    },
    contracts () {
      return this.$store.state.contracts.contracts
    },
    filteredContractsByDate () {
      return this.dateSelected
        ? this.contracts.filter(element => this.formatDate(element.contract_date) === this.formatDate(this.dateSelected))
        : this.contracts
    },
    filteredContractsByState () {
      return this.filterState
        ? this.filteredContractsByDate.filter(element => {
          return element.state.toString().includes(this.filterState)
        }) : this.filteredContractsByDate
    }
  },
  methods: {
    formatDate(date) {
      return Util.formatDate(date)
    },
    getClientName (id) {
      var name = ''
      this.clients.forEach(element => {
        if (element.id === id) name = element.brand_name
      })
      return name
    },
    deleteContractDialog (contract) {
      this.contractToDelete = contract
      this.dialog = !this.dialog
    },
    deleteContract () {
      var id = this.contractToDelete.id
      this.$store.dispatch('contracts/deleteContract', id)
        .then(() => {
          this.$toast("Adjudicación borrada correctamente.", {
            color: "success",
            x: "center",
            y: "top",
            icon: "mdi-information"
          });
          this.loadContracts()
        })
        .catch(error => {
          var errorToastMessage = "Ha ocurrido un error eliminando la adjudicación."
          Util.showErrorToast(this, error, errorToastMessage);
        })
        .finally(() => {
          this.dialog = !this.dialog
        })
    },
    contractCreate () {
      this.$router.push({
        name: "contract-create"
      });
    },
    contractEdit (id) {
      this.$router.push({
        name: "contract-edit",
        params: { id: id }
      });
    },
    loadContracts () {
      this.isLoadingData = true;
      this.$store.dispatch('contracts/getContracts')
        .then(() => {
          this.loadClients()
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando las adjudicaciones."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
    },
    loadClients () {
      this.$store.dispatch('clients/getClients')
        .then(() => {})
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de clientes."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.authUser = true
          this.isLoadingData = false;
        });
    },
  },
  created () {
    this.loadContracts()
  }
}
</script>
